import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { OTPInput } from '@/components/ui/otp-input';
import { useLanguage } from '@/contexts/LanguageContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { updateUserPhoneComprehensive } from '@/lib/firebase/queries/phone-management';
import { sendOTP, verifyOTP, resendOTP, cleanupOTP } from '@/lib/services/otp-verification';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Phone, MessageSquare, CheckCircle, ArrowLeft } from 'lucide-react';
import { useOTPMethod } from '@/hooks/use-otp-method';

interface NameInputDialogWithOTPProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  onComplete: () => void;
}

type FormStep = 'details' | 'phone-otp';

export function NameInputDialogWithOTP({ isOpen, onClose, userId, onComplete }: NameInputDialogWithOTPProps) {
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<FormStep>('details');
  
  // Form data - only phone is needed now (names are auto-translated by AI)
  const [phone, setPhone] = useState('');
  const [originalPhone, setOriginalPhone] = useState('');
  
  // OTP states
  const [otpValue, setOtpValue] = useState('');
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [isResendLoading, setIsResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpExpiry, setOtpExpiry] = useState<Date | null>(null);
  
  const { t, dir } = useLanguage();
  const { otpMethod } = useOTPMethod();
  const { userData, setUserData } = useAuth();

  const phoneRegex = /^05\d{8}$/; // 05xxxxxxxx

  // Load user data when dialog opens
  useEffect(() => {
    if (isOpen && userId) {
      loadUserData();
    }
  }, [isOpen, userId]);

  // Cooldown timer effect
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // OTP expiry timer effect
  useEffect(() => {
    if (otpExpiry) {
      const timer = setInterval(() => {
        const now = new Date();
        if (now >= otpExpiry) {
          setOtpExpiry(null);
          toast.error(t('otp.errors.otpExpired') || 'Verification code has expired');
          setCurrentStep('details');
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [otpExpiry, t]);

  const loadUserData = async () => {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      
      if (userData) {
        setPhone(userData.phone || '');
        setOriginalPhone(userData.phone || '');
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const handleDetailsSubmit = async () => {
    if (!phoneRegex.test(phone)) {
      toast.error(dir === 'rtl' ? 'رقم الجوال يجب أن يكون بصيغة 05XXXXXXXX' : 'Phone must be 05XXXXXXXX');
      return;
    }

    // If phone hasn't changed, proceed directly to save
    if (phone === originalPhone) {
      await handleFinalSave();
      return;
    }

    // Phone has changed, need OTP verification
    try {
      setIsOtpLoading(true);
      const language = dir === 'rtl' ? 'ar' : 'en';
      const result = await sendOTP(phone, language);
      
      if (result.success) {
        setCurrentStep('phone-otp');
        setOtpExpiry(new Date(Date.now() + 5 * 60 * 1000)); // 5 minutes from now
        setResendCooldown(60); // 1 minute cooldown
        toast.success(t('otp.success.otpSent') || (otpMethod === 'sms' ? 'Verification code sent via SMS' : 'Verification code sent to your WhatsApp'));
      } else {
        // Show specific error messages
        let errorMessage = result.error || t('otp.errors.sendFailed') || 'Failed to send verification code';
        
        if (result.error?.includes('already registered')) {
          errorMessage = t('otp.errors.phoneAlreadyRegistered') || errorMessage;
        } else if (result.error?.includes('already reserved')) {
          errorMessage = t('otp.errors.phoneAlreadyReserved') || errorMessage;
        }
        
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      toast.error(t('otp.errors.sendFailed') || 'Failed to send verification code');
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otpValue.length !== 4) {
      toast.error(t('otp.errors.otpInvalid') || 'Please enter a valid 4-digit code');
      return;
    }

    try {
      setIsOtpLoading(true);
      const result = await verifyOTP(phone, otpValue);
      
      if (result.success) {
        toast.success(t('otp.success.phoneVerified') || 'Phone number verified successfully');
        await handleFinalSave();
      } else {
        toast.error(result.error || t('otp.errors.otpInvalid') || 'Invalid verification code');
        setOtpValue(''); // Clear invalid OTP
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      toast.error(t('otp.errors.otpInvalid') || 'Invalid verification code');
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      setIsResendLoading(true);
      const language = dir === 'rtl' ? 'ar' : 'en';
      const result = await resendOTP(phone, language);
      
      if (result.success) {
        setOtpExpiry(new Date(Date.now() + 5 * 60 * 1000)); // 5 minutes from now
        setResendCooldown(60); // 1 minute cooldown
        setOtpValue(''); // Clear current OTP
        toast.success(t('otp.success.otpSent') || (otpMethod === 'sms' ? 'New verification code sent via SMS' : 'New verification code sent to your WhatsApp'));
      } else {
        toast.error(result.error || t('otp.errors.sendFailed') || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Error resending OTP:', error);
      toast.error(t('otp.errors.sendFailed') || 'Failed to send verification code');
    } finally {
      setIsResendLoading(false);
    }
  };

  const handleFinalSave = async () => {
    try {
      setLoading(true);
      
      if (phone !== originalPhone) {
        // Use comprehensive phone update function
        const phoneUpdateResult = await updateUserPhoneComprehensive(userId, originalPhone, phone);
        
        if (!phoneUpdateResult.success) {
          toast.error(phoneUpdateResult.error || (dir === 'rtl' ? 'رقم الجوال مستخدم بالفعل' : 'Phone number already in use'));
          return;
        }
        
        // Clean up OTP verification record
        await cleanupOTP(phone);
        
        // Update timestamp (phone already updated by comprehensive function)
        await updateDoc(doc(db, 'users', userId), {
          updatedAt: new Date()
        });
      } else {
        // Phone hasn't changed, just update timestamp
        await updateDoc(doc(db, 'users', userId), {
          phone,
          updatedAt: new Date()
        });
      }
      
      // Update local auth context
      try {
        setUserData?.({ ...(userData as any), phone });
      } catch {}
      
      // Mark profile as completed
      try {
        localStorage.setItem(`profileCompleted_${userId}`, 'true');
      } catch {}
      
      toast.success(dir === 'rtl' ? 'تم تحديث البيانات بنجاح' : 'Profile updated successfully');
      onComplete();
      onClose();
      
      // Reset states
      setCurrentStep('details');
      setOtpValue('');
      setOtpExpiry(null);
      setResendCooldown(0);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t('auth.nameUpdateError') || (dir === 'rtl' ? 'حدث خطأ أثناء تحديث البيانات' : 'Failed to update details'));
    } finally {
      setLoading(false);
    }
  };

  const handleBackToDetails = () => {
    setCurrentStep('details');
    setOtpValue('');
    setOtpExpiry(null);
    setResendCooldown(0);
  };

  const renderTimeRemaining = () => {
    if (!otpExpiry) return null;
    
    const now = new Date();
    const remaining = Math.max(0, Math.floor((otpExpiry.getTime() - now.getTime()) / 1000));
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    
    return (
      <p className="text-sm text-muted-foreground text-center">
        {t('otp.info.codeExpiry')?.replace('{{minutes}}', `${minutes}:${seconds.toString().padStart(2, '0')}`) || 
          `Code expires in ${minutes}:${seconds.toString().padStart(2, '0')}`}
      </p>
    );
  };

  const getStepIcon = () => {
    switch (currentStep) {
      case 'details':
        return <CheckCircle className="h-5 w-5" />;
      case 'phone-otp':
        return <MessageSquare className="h-5 w-5" />;
      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 'details':
        return t('completeProfile') || (dir === 'rtl' ? 'إكمال الملف الشخصي' : 'Complete Profile');
      case 'phone-otp':
        return t('otp.title') || 'Phone Verification';
      default:
        return '';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className={`sm:max-w-[425px] ${dir === 'rtl' ? 'rtl' : ''} [&>button]:hidden`} dir={dir}>
        <DialogHeader className={dir === 'rtl' ? 'text-right' : 'text-left'}>
          <DialogTitle className={`flex items-center gap-2 ${dir === 'rtl' ? 'text-right justify-end' : 'text-left'}`}>
            {getStepIcon()}
            {getStepTitle()}
          </DialogTitle>
          {currentStep === 'phone-otp' && (
            <p className="text-sm text-muted-foreground">
              {otpMethod === 'sms'
              ? (t('otp.smsSubtitle') || "We've sent a verification code via SMS")
              : (t('otp.subtitle') || "We've sent a verification code to your WhatsApp")
            }
            </p>
          )}
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {currentStep === 'details' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone" className={dir === 'rtl' ? 'text-right' : ''}>
                  {t('auth.phone') || (dir === 'rtl' ? 'رقم الجوال' : 'Phone')}
                </Label>
                <Input
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="05XXXXXXXX"
                  dir="ltr"
                  className="text-left placeholder:text-gray-400 dark:placeholder:text-gray-500"
                  inputMode="numeric"
                  maxLength={10}
                  autoFocus
                />
                {phone !== originalPhone && phone && (
                  <p className="text-xs text-amber-600">
                    {dir === 'rtl' 
                      ? 'سيتم إرسال رمز تحقق لهذا الرقم' 
                      : 'A verification code will be sent to this number'}
                  </p>
                )}
              </div>
            </div>
          )}

          {currentStep === 'phone-otp' && (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  {otpMethod === 'sms'
                    ? (t('otp.info.checkSms') || 'Please check your SMS messages')
                    : (t('otp.info.checkWhatsapp') || 'Please check your WhatsApp messages')
                  } ({phone})
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t('otp.otpLabel') || 'Verification Code'}
                  </label>
                  <OTPInput
                    value={otpValue}
                    onChange={setOtpValue}
                    disabled={isOtpLoading}
                    dir={dir}
                  />
                </div>

                {renderTimeRemaining()}

                <Button 
                  onClick={handleVerifyOTP}
                  disabled={isOtpLoading || otpValue.length !== 4}
                  className="w-full"
                >
                  {isOtpLoading ? (
                    <>
                      <Loader2 className={`h-4 w-4 animate-spin ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                      {t('otp.verifyOtp') || 'Verifying...'}
                    </>
                  ) : (
                    t('otp.verifyOtp') || 'Verify Code'
                  )}
                </Button>

                <div className="flex gap-2">
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={handleBackToDetails}
                    disabled={isOtpLoading}
                    className="flex-1"
                  >
                    <ArrowLeft className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2 rotate-180' : 'mr-2'}`} />
                    {t('otp.backToPhone') || 'Back'}
                  </Button>

                  <Button 
                    type="button"
                    variant="outline"
                    onClick={handleResendOTP}
                    disabled={isResendLoading || resendCooldown > 0}
                    className="flex-1"
                  >
                    {isResendLoading ? (
                      <Loader2 className={`h-4 w-4 animate-spin ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                    ) : null}
                    {resendCooldown > 0 
                      ? `${t('otp.resendOtp') || 'Resend'} (${resendCooldown}s)`
                      : t('otp.resendOtp') || 'Resend Code'
                    }
                  </Button>
                </div>
              </div>

              {resendCooldown === 0 && (
                <Alert>
                  <AlertDescription>
                    {t('otp.info.resendAvailable') || "Didn't receive the code? You can resend after 1 minute"}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>

        {currentStep === 'details' && (
          <div className={`flex ${dir === 'rtl' ? 'justify-start' : 'justify-end'}`}>
            <Button onClick={handleDetailsSubmit} disabled={loading || isOtpLoading} className="w-full">
              {(loading || isOtpLoading) ? (
                <>
                  <Loader2 className={`h-4 w-4 animate-spin ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                  {isOtpLoading 
                    ? (t('otp.sendOtp') || 'Sending...')
                    : (t('common.saving') || (dir === 'rtl' ? 'جاري الحفظ...' : 'Saving...'))
                  }
                </>
              ) : (
                phone !== originalPhone && phone 
                  ? (t('otp.sendOtp') || 'Send Verification Code')
                  : (t('common.save') || (dir === 'rtl' ? 'حفظ' : 'Save'))
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
